import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../lib/api';
import type {
  MessageThread,
  MessageConfirmedStatusResponse,
  SendMessageRequest,
  SendMessageResponse,
  FamilyMessage,
} from '../../lib/api';
import type { ApiError } from '@shared/types';

/**
 * メッセージスレッドを取得するカスタムフック
 */
export const useMessagesThread = (
  patientId: number | null,
  options?: { limit?: number; enabled?: boolean }
) => {
  return useQuery<MessageThread>({
    queryKey: ['messages', 'thread', patientId],
    queryFn: async () => {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      return await apiClient.getMessagesThread(patientId, { limit: options?.limit });
    },
    enabled: patientId !== null && !!apiClient.getIdToken() && (options?.enabled ?? true),
    staleTime: 30000, // 30秒
    refetchInterval: 60000, // 1分ごとに自動更新
    retry: 1,
  });
};

/**
 * 確認済みステータスの軽量ポーリングhook
 * 15秒間隔でステータスのみを取得し、既存キャッシュを直接更新する
 */
export const useMessageConfirmedStatus = (patientId: number | null) => {
  const queryClient = useQueryClient();

  const query = useQuery<MessageConfirmedStatusResponse>({
    queryKey: ['messages', 'confirmed-status', patientId],
    queryFn: async () => {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      return await apiClient.getMessageConfirmedStatus(patientId);
    },
    enabled: patientId !== null && !!apiClient.getIdToken(),
    staleTime: 10000, // 10秒
    refetchInterval: 15000, // 15秒ごとにポーリング
    refetchIntervalInBackground: false, // バックグラウンド時は停止
  });

  // ステータス変更時に既存のスレッドキャッシュを更新
  useEffect(() => {
    if (!query.data || !patientId) return;

    const statuses = query.data.statuses;
    const currentThread = queryClient.getQueryData<MessageThread>([
      'messages', 'thread', patientId,
    ]);

    if (!currentThread?.threads) return;

    // statusesをMapに変換してO(1)探索
    const statusMap = new Map<string, string | null>();
    for (const s of statuses) {
      statusMap.set(`${s.thread_id}:${s.message_id}`, s.confirmed_at);
    }

    // confirmed_atの変更を検出してキャッシュを更新（確認追加・取り消し双方向）
    let hasChange = false;
    const updatedThreads = currentThread.threads.map(thread => {
      const updatedMessages = thread.messages.map(msg => {
        if (msg.direction !== 'outgoing') return msg;

        const key = `${thread.thread_id}:${msg.message_id}`;
        if (!statusMap.has(key)) return msg;

        const confirmedAt = statusMap.get(key);

        if (confirmedAt && !msg.confirmed_at) {
          // 確認済みに変更
          hasChange = true;
          return { ...msg, confirmed_at: confirmedAt };
        }
        if (!confirmedAt && msg.confirmed_at) {
          // 確認取り消し
          hasChange = true;
          return { ...msg, confirmed_at: undefined };
        }
        return msg;
      });
      return { ...thread, messages: updatedMessages };
    });

    if (hasChange) {
      queryClient.setQueryData<MessageThread>(
        ['messages', 'thread', patientId],
        { ...currentThread, threads: updatedThreads }
      );
    }
  }, [query.data, patientId, queryClient]);

  return query;
};

/**
 * メッセージ送信用のミューテーションフック
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation<SendMessageResponse, ApiError, SendMessageRequest>({
    mutationFn: async (data: SendMessageRequest) => {
      return await apiClient.sendMessage(data);
    },
    /**
     * 応答が返るまではキャッシュに出さない（楽観的更新をしない）。
     *
     * 送信者名は応答で初めて分かるため、先に出すと名前なしのメッセージが一瞬表示される。
     * 送信完了ダイアログの後に表示されれば体験上問題ない。進行中の再取得だけは止めておき、
     * 反映前の応答で onSuccess の差し込みが上書きされないようにする
     */
    onMutate: async (newMessage) => {
      const key = ['messages', 'thread', newMessage.patient_id];
      // キャッシュが無いときに止めると初回取得まで取り消され、取り直されないまま空になる（onSuccess と同じ理由）
      if (!queryClient.getQueryData<MessageThread>(key)?.threads) return;
      await queryClient.cancelQueries({ queryKey: key });
    },
    onError: (err) => {
      // BE が service_ended 由来の 403 を返した場合、linkedPatients を再取得して
      // FE 側のトルツメ判定を最新の service_end_date に基づき更新する。
      // ApiClient の interceptor が AxiosError を ApiError に変換するため、
      // useMutation の error 型引数は ApiError に揃えてある（キャスト不要）。
      const reason = err.details?.reason;
      if (err.statusCode === 403 && reason === 'service_ended') {
        queryClient.invalidateQueries({ queryKey: ['linkedPatients'] });
      }
    },
    /**
     * 送信結果をキャッシュへ確定値として差し込む。
     *
     * **サーバへ取り直さない。** スレッド一覧は `PatientIdIndex`（GSI・結果整合）で読むため、
     * 送信直後に再取得すると反映前の応答で上書きされ、出したばかりの自分のメッセージが
     * 消えて次の定期更新（60 秒）まで戻らない。自分が書いた内容を知るためにサーバへ
     * 聞きに行く必要はなく、レスポンスが `message_id` / `created_at` / `thread_id` / `sender_name` を返している
     * （`docs/architecture/data/family-messaging.md` §6 / PR9 計画書）。
     *
     * 強整合読み取りでは解決しない — GSI は `ConsistentRead` をサポートせず、
     * `family_message_threads` は PK が `thread_id` のため利用者単位で base table から
     * 引く経路が無い。
     *
     * キャッシュがまだ無い（やりとり画面を開く前にホームから返信した）場合は差し込まない。
     * 初回取得が GSI の反映に間に合わないと一瞬見えないが、反映は通常 1 秒未満で、
     * 完了ダイアログを経てから開くため受容する（PR9 計画書）。
     */
    onSuccess: async (data, variables) => {
      const key = ['messages', 'thread', variables.patient_id];
      // キャッシュが無ければ差し込まないので、取得も止めない。進行中なのは初回取得で、
      // 止めると取得前の状態に戻ったまま自動では取り直されず、一覧が最大 60 秒空になる
      // （cancelQueries は revert: true が既定。refetchOnWindowFocus も無効）
      if (!queryClient.getQueryData<MessageThread>(key)?.threads) return;

      // 送信中に始まった定期取得（60 秒）は onMutate では止められない。
      // 反映前の応答が差し込み後に届いて上書きしないよう、ここでも止めてから書き込む
      await queryClient.cancelQueries({ queryKey: key });
      const current = queryClient.getQueryData<MessageThread>(key);
      if (!current?.threads) return;

      const saved: FamilyMessage = {
        message_id: data.message_id,
        direction: 'outgoing',
        content: variables.content,
        created_at: data.created_at,
        ...(data.sender_name && { sender_name: data.sender_name }),
        ...(variables.reply_to && { reply_to: variables.reply_to }),
      };
      const threadId = data.thread_id ?? variables.thread_id;

      const existing = current.threads.find((t) => t.thread_id === threadId);
      const threads = existing
        ? current.threads.map((t) =>
            t.thread_id === threadId
              ? {
                  ...t,
                  // 応答より先に定期取得が保存済みの分を取ってきていれば、追加せず値だけ合わせる
                  // （二重表示と React のキー重複を避ける。位置と confirmed_at は既存のまま）
                  messages: t.messages.some((m) => m.message_id === saved.message_id)
                    ? t.messages.map((m) => (m.message_id === saved.message_id ? { ...m, ...saved } : m))
                    : [...t.messages, saved],
                }
              : t
          )
        : // 新規スレッド作成の経路。レスポンスの thread_id でスレッドごと足す
          [
            ...current.threads,
            {
              thread_id: threadId ?? String(data.message_id),
              hide_from_patient: variables.hide_from_patient ?? false,
              messages: [saved],
            },
          ];

      queryClient.setQueryData<MessageThread>(key, { ...current, threads });
    },
    onSettled: (_data, error, variables) => {
      // **成功時は再取得しない**（上の onSuccess を打ち消してしまうため）。
      // 失敗時だけサーバの状態に合わせ直す
      if (!error) return;
      queryClient.invalidateQueries({
        queryKey: ['messages', 'thread', variables.patient_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['messages', 'threads', variables.patient_id],
      });
      if (variables.thread_id) {
        queryClient.invalidateQueries({
          queryKey: ['messages', 'thread-messages', variables.thread_id],
        });
      }
    },
  });
};

export default useMessagesThread;
